import { createExchangeTokenRedirect } from '../../../admin-portal/src/services/exchangeTokenService.js'
import { TARGET_APPS } from '../../enums/target-apps.js'
import { showSnackbar } from '../../state/snackbar.js'
import { loadingSpinner } from '../../state/loading-spinner.js'
import { createSingleFlight } from './single-flight.js'

/**
 * Cross-portal hop from the admin portal into the new Site Scanner.
 *
 * Deliberately does NOT go through ./nav-mode.js, unlike the dashboard hop.
 * Both target apps read a query param named `nav`, but their vocabularies are
 * inverted:
 *
 *   param absent   consent-banners: standalone   site-scanner: ADMIN chrome
 *   nav=embedded   consent-banners: admin        site-scanner: ignored
 *   nav=standalone consent-banners: n/a          site-scanner: BANNERS chrome
 *
 * The site scanner already defaults to the admin-portal navbar when the param
 * is absent, which is exactly what an entry from this portal wants, and its
 * exchange route expires any stale chrome cookie left by a prior consent
 * banners visit. So the correct hop is a bare redirect. Appending `embedded`
 * would be inert but would imply a behaviour it does not have; appending
 * `standalone` would hand the user the wrong navbar entirely.
 */
export const SITE_SCANNER_NAVIGATION_TARGET_APP = TARGET_APPS.SITE_SCANNER
export const EXCHANGE_TOKEN_REDIRECT_ERROR_MESSAGE = 'Error authenticating portal access. Please try again later.'

export async function getSiteScannerRedirectUrl () {
	const redirectUrl = await createExchangeTokenRedirect(TARGET_APPS.SITE_SCANNER)
	if (!redirectUrl) {
		console.error('[site-scanner-navigation] exchange token redirect unavailable')
		showSnackbar({ text: EXCHANGE_TOKEN_REDIRECT_ERROR_MESSAGE, color: 'red' })
		return null
	}
	return redirectUrl
}

const tokenRequest = createSingleFlight()

// A page restored from the back/forward cache brings this module's state back
// with it, latch and overlay included, both still held for a hop that has
// already happened. Without this the user would return to the admin portal to
// find a stuck overlay over a menu item that no longer responds. The sibling
// helpers have the same exposure through the overlay alone.
if (typeof window !== 'undefined') {
	window.addEventListener('pageshow', event => {
		if (!event.persisted) return
		tokenRequest.release()
		loadingSpinner.value = false
	})
}

export async function navigateToSiteScannerPortal () {
	// The latch, not the overlay, is what stops a duplicate token being minted.
	// The overlay blocks pointer events, but Vuetify gives a list item with a
	// click listener tabindex="0" and turns Enter into a click, so a focused
	// item can still fire from the keyboard while the overlay is up.
	if (!tokenRequest.tryBegin()) return false
	// Raised before the request rather than after it, which is where the sibling
	// helpers put it. The token round trip is the part the user actually waits
	// on, so signalling only once it has returned leaves the menu looking dead
	// for the duration.
	loadingSpinner.value = true
	let redirectUrl = null
	try {
		redirectUrl = await getSiteScannerRedirectUrl()
	} finally {
		// Released only when we are staying on the page. On success both stay held
		// until the browser leaves, so the user sees neither a bare page mid-hop
		// nor a re-armed menu item. In a finally so an unexpected throw cannot
		// strand them and lock the UI.
		if (!redirectUrl) {
			loadingSpinner.value = false
			tokenRequest.release()
		}
	}
	if (!redirectUrl) return false
	window.location.href = redirectUrl
	return true
}
